
import React, { useState } from "react";
import { Avatar, Button, IconButton, Tooltip } from "@mui/material";
import { Helmet } from "react-helmet";
import { GridColDef } from "@mui/x-data-grid";
import { useCookies } from "react-cookie";
import { AddBox, DeleteForever as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";

import * as UserService from "../../services/user.service";
import BlankAvatar from "../../assets/png/blank-avatar.png";
import UserWrapper from "../../wrappers/user.wrapper";
import { CheckIfUserHasPermission } from "../../utils/access";
import { CREATE_USER_ALLOWED_ACCESSES, DELETE_USER_ALLOWED_ACCESSES } from "../../constants/access.constant";
import GenericDialog from "../../components/Dialogs/Generic";
import UserForm from "../../components/Forms/UserForm";
import { useUsers } from "../../hooks";
import { PromiseToast, SuccessToast } from "../../utils/toaster";
import { User } from "../../types/user.types";
import AODataTable from "../../components/Table/DataTable";
import "./Users.styles.scss";


export default function Users() {
    const [showUserFormDialog, setShowUserFormDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [userToEdit, setUserToEdit] = useState<User>();
    const [userToDelete, setUserToDelete] = useState<User>();
    const [cookies] = useCookies(["session_token"]);
    const user = UserWrapper.get();
    const { users, loadingUsers } = useUsers();
    const queryClient = useQueryClient();

    const isUserAllowedToCreate = CheckIfUserHasPermission(
        user.access!,
        CREATE_USER_ALLOWED_ACCESSES
    );

    const isUserAllowedToEdit = isUserAllowedToCreate;
    const isUserAllowedToDelete = CheckIfUserHasPermission(user.access!, DELETE_USER_ALLOWED_ACCESSES);

    const columns: GridColDef<User>[] = [
        {
            field: "Picture",
            headerName: "Foto",
            width: 70,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => <Avatar alt="User picture" src={params.value || BlankAvatar} sx={{ height: "2rem", width: "2rem" }} />
        },
        { field: "FirstName", headerName: "Nome", width: 200, align: "center", headerAlign: "center", },
        { field: "LastName", headerName: "Sobrenome", width: 200, align: "center", headerAlign: "center", },
        { field: "Email", headerName: "E-mail", width: 300, maxWidth: 500, align: "center", headerAlign: "center", },
        {
            field: "Actions",
            headerName: "Ações",
            width: 150,
            maxWidth: 300,
            align: "center",
            headerAlign: "center",
            colSpan: 4,
            renderCell: (params) => (
                <span>
                    <Tooltip
                        title={!isUserAllowedToEdit && "Você não tem permissão pra fazer isso"}
                        disableHoverListener={isUserAllowedToEdit}
                        arrow
                    >
                        <span>
                            <IconButton disabled={!isUserAllowedToEdit} onClick={(e) => handleEditButtonClick(params.row)}>
                                <EditIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={
                            !isUserAllowedToDelete ?
                                "Você não tem permissão pra fazer isso" :
                               user.data?.Email === params.row.Email && "Você não pode se deletar!"
                        }
                        disableHoverListener={isUserAllowedToDelete && user.data?.Email !== params.row.Email}
                        arrow
                    >
                        <span>
                            <IconButton disabled={!isUserAllowedToDelete || user.data?.Email === params.row.Email} onClick={(e) => { toggleDeleteDialog(params.row); }}>
                                <DeleteIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </span>
            ),
        },
    ];

    const handleEditButtonClick = (u: User) => {
        setIsEditing(true);
        setUserToEdit(u);
        toggleUserFormDialog();
    };
    const handleCreateButtonClick = (e: React.MouseEvent<HTMLElement>) => {
        setIsEditing(false);
        toggleUserFormDialog();
    };
    const toggleUserFormDialog = (submitted?: boolean) => {
        if (typeof submitted === "boolean" && submitted) {
            queryClient.invalidateQueries(['users']).then(() => SuccessToast(`Usuário ${isEditing ? 'atualizado' : 'criado'}!`));
        }
        setShowUserFormDialog(!showUserFormDialog);
    };
    const toggleDeleteDialog = (clickedUser?: User) => {
        if (clickedUser) setUserToDelete(clickedUser);
        setShowDeleteDialog(!showDeleteDialog);
    };
    const deleteUser = async () => {
        if (userToDelete) {
            toggleDeleteDialog();
            await PromiseToast(
                UserService.DeleteUser(userToDelete.Id, cookies.session_token),
                {
                    error: "Não conseguimos deletar esse usuário! Tente novamente!",
                    pending: "Deletando usuário...",
                    success: "Usuário deletado!"
                }
            );
            await queryClient.invalidateQueries(['users']);
        }
    };

    return (
        <>
            <Helmet title="Admin ONE | Usuários" />
            <GenericDialog
                onClose={toggleDeleteDialog}
                open={showDeleteDialog}
                title="Deletar Usuário"
                useConfirmAndCancelButtons
                onConfirm={deleteUser}
            >
                <p style={{ fontSize: "1.2rem" }}>Tem certeza que deseja deletar <strong>{userToDelete?.FirstName}</strong>?</p>
            </GenericDialog>
            <GenericDialog
                onClose={toggleUserFormDialog}
                open={showUserFormDialog}
                title={`${isEditing ? 'Editar' : 'Adicionar'} Usuário`}
            >
                <UserForm isEditing={isEditing} onClose={toggleUserFormDialog} user={userToEdit} />
            </GenericDialog>
            <AODataTable
                useDefaultPreComponent
                columns={columns}
                rows={users!}
                isLoading={loadingUsers}
                title="Lista de Usuários"
                preComponentAction={
                    <Tooltip
                        title={!isUserAllowedToCreate && "Você não tem permissão pra fazer isso"}
                        disableHoverListener={isUserAllowedToCreate}
                        arrow
                    >
                        <span>
                            <Button
                                startIcon={<AddBox />}
                                variant="contained"
                                sx={{ minWidth: "14rem", maxWidth: "16rem" }}
                                disabled={!isUserAllowedToCreate}
                                classes={{ disabled: "create-user-btn-disabled" }}
                                onClick={handleCreateButtonClick}
                            >
                                Adicionar Usuário
                            </Button>
                        </span>
                    </Tooltip>
                }
            />
        </>
    );
}
